import {
  Currency,
  currencySymbols,
  ImpactOrderReportPeriod,
} from "@ecologi/shared";
import { DateTime } from "luxon";
import { theme } from "../theme";
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  ChartOptions,
  Legend,
  ChartData,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useIsMobileUp } from "../utils";

const increaseLegendSpacing = {
  id: "increase-legend-spacing",
  beforeInit(chart: any) {
    const originalFit = chart.legend.fit;
    chart.legend.fit = function fit() {
      originalFit.bind(chart.legend)();
      this.height += 22;
    };
  },
};

Chart.register([
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  increaseLegendSpacing,
]);

const sharedBarOptions = {
  maxBarThickness: 12,
  borderRadius: 10,
  minBarLength: 6,
};

const sharedTickOptions = {
  padding: 10,
  font: {
    size: 12,
    family: "Sofia Pro",
  },
  color: theme.colors.charcoal,
};

type props = {
  usageData: ImpactOrderReportPeriod[];
  currency: Currency;
};

export const APIUsageGraph = ({ usageData, currency }: props) => {
  const isMobileUp = useIsMobileUp();

  const labels: string[] = [];
  const paidTrees: Array<number | null> = [];
  const pendingTrees: Array<number | null> = [];
  const paidCarbonOffsets: Array<number | null> = [];
  const pendingCarbonOffsets: Array<number | null> = [];
  const paidCarbonRemoval: Array<number | null> = [];
  const pendingCarbonRemoval: Array<number | null> = [];
  const paidHabitatRestoration: Array<number | null> = [];
  const pendingHabitatRestoration: Array<number | null> = [];

  usageData.forEach(({ periodStart, paid, pending }) => {
    const date = DateTime.fromISO(periodStart).toISO();
    labels.push(date);
    paidTrees.push(Number(paid.TREES) / 100 || null);
    pendingTrees.push(Number(pending.TREES) / 100 || null);
    paidCarbonOffsets.push(Number(paid.CARBON_OFFSETS) / 100 || null);
    pendingCarbonOffsets.push(Number(pending.CARBON_OFFSETS) / 100 || null);
    paidCarbonRemoval.push(Number(paid.CARBON_REMOVAL) / 100 || null);
    pendingCarbonRemoval.push(Number(pending.CARBON_REMOVAL) / 100 || null);
    paidHabitatRestoration.push(Number(paid.HABITAT_RESTORATION) / 100 || null);
    pendingHabitatRestoration.push(
      Number(pending.HABITAT_RESTORATION) / 100 || null
    );
  });

  const hasData = (arr: Array<number | null>) => arr.some((v) => v !== null);

  const allDatasets = [
    {
      label: "Trees",
      data: paidTrees,
      backgroundColor: theme.colors.forestLight,
      ...sharedBarOptions,
    },
    {
      label: "Carbon Avoidance",
      data: paidCarbonOffsets,
      backgroundColor: theme.colors.buttercup,
      ...sharedBarOptions,
    },
    {
      label: "Carbon Removal",
      data: paidCarbonRemoval,
      backgroundColor: theme.colors.rose,
      ...sharedBarOptions,
    },
    {
      label: "Habitat Restoration",
      data: paidHabitatRestoration,
      backgroundColor: theme.colors.lavender,
      ...sharedBarOptions,
    },
    {
      label: "Pending Trees",
      data: pendingTrees,
      backgroundColor: theme.colors.charcoalLight,
      ...sharedBarOptions,
    },
    {
      label: "Pending Carbon Avoidance",
      data: pendingCarbonOffsets,
      backgroundColor: theme.colors.cloud,
      ...sharedBarOptions,
    },
    {
      label: "Pending Carbon Removal",
      data: pendingCarbonRemoval,
      backgroundColor: theme.colors.roseLight,
      ...sharedBarOptions,
    },
    {
      label: "Pending Habitat Restoration",
      data: pendingHabitatRestoration,
      backgroundColor: theme.colors.lavenderLight,
      ...sharedBarOptions,
    },
  ];

  const graphData: ChartData<"bar"> = {
    labels,
    datasets: allDatasets.filter((dataset) => hasData(dataset.data)),
  };

  const chartOptions: ChartOptions<"bar"> = {
    scales: {
      x: {
        grid: {
          display: false,
          drawOnChartArea: false,
        },
        stacked: true,
        ticks: {
          ...sharedTickOptions,
          callback: function (value) {
            const date = DateTime.fromISO(this.getLabelForValue(Number(value)));
            return date.toFormat("LLL");
          },
        },
      },
      y: {
        border: {
          display: false,
        },
        stacked: true,
        grid: {
          ...sharedTickOptions,
          color: function (context) {
            if (context.tick && context.tick.label !== "") {
              return theme.colors.mistDark;
            }
            return "transparent";
          },
        },
        grace: "20%",
        ticks: {
          ...sharedTickOptions,
          callback: function (value, index) {
            if (index % 2 === 0) {
              return `${currencySymbols[currency]} ${this.getLabelForValue(
                Number(value)
              )}`;
            }
            return "";
          },
        },
      },
    },
    plugins: {
      tooltip: {
        multiKeyBackground: "transparent",
        boxPadding: 6,
        titleFont: {
          family: "Sofia Pro",
          size: 16,
        },
        bodyFont: {
          family: "Sofia Pro",
          size: 14,
        },
        usePointStyle: true,
        padding: 12,
        borderWidth: 0,
        bodySpacing: 6,
        titleSpacing: 12,
        mode: "index",
        callbacks: {
          title: function (tooltipItems) {
            const label = tooltipItems[0]?.label;
            if (label) {
              const date = DateTime.fromISO(label);
              return date.toFormat("LLLL");
            }
            return "";
          },
          label: function (tooltipItem) {
            const datasetLabel = tooltipItem.dataset.label || "";
            const value = tooltipItem.raw;
            return `${datasetLabel}: ${currencySymbols[currency]}${value || 0}`;
          },
        },
      },
      legend: {
        display: true,
        position: "top",
        align: isMobileUp ? "end" : "start",
        onClick: () => {},
        labels: {
          padding: isMobileUp ? 14 : 12,
          font: {
            family: "Sofia Pro",
            size: isMobileUp ? 14 : 12,
          },
          pointStyle: "circle",
          usePointStyle: true,
          color: theme.colors.charcoal,
        },
      },
    },
  };

  return (
    <Bar
      data={graphData}
      options={chartOptions}
      height={isMobileUp ? 230 : 300}
    />
  );
};
